import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classes from 'new/components/settings/VendorBuilder/VendorBuilder.module.scss';
import { Section } from 'new/components/settings/VendorBuilder/components/Section';
import { Stack } from 'new/elements/Stack';
import Typography from 'new/elements/Typography';
import { useVendorBuilder } from 'new/components/settings/VendorBuilder/VendorBuilderContext';
import { SourceIterations } from 'new/components/settings/VendorBuilder/Endpoints/SourceIterations';
import { ResourceParameters } from 'new/components/settings/VendorBuilder/ResourceParameters/ResourceParameters';
import { Select } from 'new/elements/Select';
import { SinkTemplate } from 'new/components/settings/VendorBuilder/Endpoints/SinkTemplate';
import { FormBuilder } from 'new/components/settings/VendorBuilder/FormControlBuilder';

interface EndpointProps {
  index: number;
}

const resourceTypes = [
  {
    title: 'Source Template',
    value: 'SOURCE',
  },
  {
    title: 'Destination Template',
    value: 'SINK',
  },
];

const fields = (index) => [
  {
    type: 'text',
    required: true,
    label: (
      <>
        Endpoint name <code>vendor_endpoint[{index}].name</code>
      </>
    ),
    prefix: { vendorPath: 'name' },
    name: `endpoints[${index}].name`,
    helperText: 'Unique name (e.g.: vendor_name.get_all_items)',
    description: (
      <Stack padding="16px">
        <Typography variant="h6">
          <>
            Endpoint name <code>vendor_endpoint[{index}].name</code>
          </>
        </Typography>
        <Typography variant="body2">
          Set a unique name for this endpoint. This is the <code>name</code> property of the{' '}
          <code>vendor_endpoint</code> object in the template spec.
          <br />
        </Typography>
        <Typography variant="body2">
          <b>Recommendation:</b> To ensure unique names, prefix it with the{' '}
          <code>vendor.name_</code>. Ex: <code>li_mktg_api_</code> for LinkedIn Marketing API.
          <br /> Name choice has no impact other than it being the unique index. So use anything,
          maybe something close to the type of api call to be performed.
        </Typography>
      </Stack>
    ),
  },
  {
    type: 'text',
    required: true,
    label: (
      <>
        Display Name<code>vendor_endpoint[{index}].display_name</code>
      </>
    ),
    name: `endpoints[${index}].display_name`,
    description: (
      <Stack padding="16px">
        <Typography variant="h6">
          <>
            Display Name <code>vendor_endpoint[{index}].display_name</code>
          </>
        </Typography>
        <Typography variant="body2">
          Set the name user will see when choosing this endpoint. This is the{' '}
          <code>display_name</code> property of the <code>vendor_endpoint</code> object in the
          template spec.
          <br />
        </Typography>
        <Typography variant="body2">
          <b>Recommendation:</b> This is visible to the user and is supposed to guide them with{' '}
          <em>What will this endpoint achieve.</em>. Use words that are easier for user of that 3rd
          party product to understand. Keep it short and precise, use description for more details.
        </Typography>
      </Stack>
    ),
  },
  {
    type: 'text',
    label: (
      <>
        Description<code>vendor_endpoint[{index}].description</code>
      </>
    ),
    name: `endpoints[${index}].description`,
    description: (
      <Stack padding="16px">
        <Typography variant="h6">
          <>
            Description <code>vendor_endpoint[{index}].description</code>
          </>
        </Typography>
        <Typography variant="body2">
          We also have room to add about 200 character description about the endpoint that makes it
          easier for the user to understand what this endpoint is supposed to achieve.
        </Typography>
      </Stack>
    ),
  },
  {
    type: 'text',
    required: true,
    label: (
      <>
        Info URL <code>vendor_endpoint[{index}].info_url</code>
      </>
    ),
    name: `endpoints[${index}].config.info_url`,
    description: (
      <Stack padding="16px">
        <Typography variant="h6">
          <>
            Info URL <code>vendor_endpoint[{index}].info_url</code>
          </>
        </Typography>
        <Typography variant="body2">
          Add a link to the developer docs for this endpoint. If there is no specific link to this
          endpoint, use a link that can be as close as possible for this. <br /> Remember: The goal
          of this URL is to provide an easy way for user to read the relevant part of API docs and
          understand how to customize their query.
        </Typography>
      </Stack>
    ),
  },
];

export const Endpoint = (props: EndpointProps) => {
  const { index } = props;
  const [resource, setResource] = useState('SOURCE');
  const { addNameChangeListener, removeNameChangeListener, updateVendor } = useVendorBuilder();

  const onChangeName = useCallback(
    (vendorName) => {
      updateVendor((newVendor) => {
        const endpoint = newVendor.endpoints[index];
        if (!endpoint) return;
        endpoint.vendor_name = vendorName;
        endpoint.name = vendorName + '.' + (endpoint.name?.split?.('.')?.[1] || '');
      });
    },
    [index],
  );

  const onChangeTemplateType = (e) => {
    const { value } = e.target;
    setResource(value);
  };

  useEffect(() => {
    addNameChangeListener(onChangeName);

    return () => {
      removeNameChangeListener(onChangeName);
    };
  }, []);

  const endpointFields = useMemo(() => fields(index), [index]);

  return (
    <Stack className={classes.round_container}>
      <Section defaultExpanded title={`Endpoint #${index + 1}: Basics`} gap="16px">
        <FormBuilder fields={endpointFields} />
        <Select
          onChange={onChangeTemplateType}
          value={resource}
          items={resourceTypes}
          label="Type of template"
        />
      </Section>
      {resource === 'SOURCE' ? <SourceIterations endpointIndex={index} /> : null}
      {resource === 'SINK' ? <SinkTemplate endpointIndex={index} /> : null}
      <ResourceParameters endpointIndex={index} />
    </Stack>
  );
};
